import styled from 'styled-components';
import { LauncherElementData, useLauncherElementsStore } from '../../../hooks/stores/launcherElementsStore';
import { UIData, useUIStore } from '../../../hooks/stores/uiStore';
import {
  containerInputs,
  styleInputs,
  variableInputs,
} from '../../../info/launcherElementInfo';
import { ContainerPropsPanel } from './ContainerPropsPanel';
import { ContentPropsPanel } from './ContentPropsPanel';
import { StylePropsPanel } from './StylePropsPanel';
import { TextPropsPanel } from './TextPropsPanel';
import { VariablePropsPanel } from './VariablePropsPanel';
import { StylingTargetStatePanel } from './stylingTargetStatePanel/StylingTargetStatePanel';
import { useScene } from '../../../context/sceneContext';

export const Properties = () => {
  const scene = useScene();
  const [UIData] = useUIStore<UIData>();
  const [launcherElementsData] = useLauncherElementsStore<Record<string, LauncherElementData>>();
  const inputs = scene.getSelectedElementsPropInputs();

  const isHidden = UIData.selectedElementIDs.length === 0;

  const isVariablePanelVisible = inputs.some((input) =>
    variableInputs.includes(input)
  );
  const isContainerPanelVisible = inputs.some((input) =>
    containerInputs.includes(input)
  );
  const isTextPanelVisible = inputs.some((input) => input === 'textInput');
  const isStylePanelVisible = inputs.some((input) =>
    styleInputs.includes(input)
  );

  let isContentPanelVisible = false;
  if (UIData.selectedElementIDs.length === 1) {
    const element = launcherElementsData[UIData.selectedElementIDs[0]];
    if (
      element?.props?.children?.length === 1 &&
      element?.props?.children?.[0]?.type === 'text'
    ) {
      isContentPanelVisible = true;
    }
  }

  return (
    <ScrollContainer className="scroll">
      <Container $isHidden={isHidden}>
        <InnerContainer>
          {isVariablePanelVisible && (
            <PropertiesPanelContainer>
              <VariablePropsPanel />
            </PropertiesPanelContainer>
          )}

          {isContentPanelVisible && (
            <PropertiesPanelContainer>
              <ContentPropsPanel />
            </PropertiesPanelContainer>
          )}

          {isContainerPanelVisible && (
            <PropertiesPanelContainer>
              <ContainerPropsPanel />
            </PropertiesPanelContainer>
          )}

          {isTextPanelVisible && (
            <PropertiesPanelContainer>
              <TextPropsPanel />
            </PropertiesPanelContainer>
          )}

          <StylingTargetStatePanel />

          {isStylePanelVisible && (
            <PropertiesPanelContainer>
              <StylePropsPanel />
            </PropertiesPanelContainer>
          )}
        </InnerContainer>
      </Container>
    </ScrollContainer>
  );
};

const ScrollContainer = styled.div`
  box-sizing: border-box;
  position: absolute;
  top: 40px;
  right: 0;
  width: 300px;
  height: calc(100vh - 58px);
  overflow-x: hidden;
  overflow-y: auto;

  padding: var(--padding-1);
  padding-right: calc(var(--padding-1) - 6px);
  margin-right: 3px;

  pointer-events: none;
  & > * {
    pointer-events: all;
  }
`;

const Container = styled.aside<{ $isHidden?: boolean }>`
  box-sizing: border-box;
  width: 300px;
  padding-right: 16px;

  display: flex;
  flex-direction: column;

  ${({ $isHidden }) => $isHidden && `display: none;`}
  animation: slideLeft 1s cubic-bezier(0.075, 0.82, 0.165, 1);
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: calc(var(--gap-1) / 2);
`;

const PropertiesPanelContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: calc(var(--padding-1) / 2);

  font-family: var(--font-1);
  color: var(--main-2);
  font-size: 13px;

  background-color: var(--neutral-2);
  border: 1px solid var(--neutral-3);
  border-radius: var(--radius-1);
`;
